import React, { useState } from "react";
import { Box, Stack, Typography, Paper, Chip, IconButton, Collapse, Button, CircularProgress } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { QuizStyledCard } from "../../shared/QuizStyledCard";
import { COLORS } from "../constants";
import type { PaymentMethod, ModalType } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  paymentMethods: PaymentMethod[];
  onOpenModal: (type: ModalType, pm: PaymentMethod) => void;
}

export function PaymentMethodsSection({ paymentMethods, onOpenModal }: Props) {
  const [show, setShow] = useState(true);

  if (paymentMethods.length === 0) return null;

  return (
    <QuizStyledCard>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setShow(!show)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CreditCardIcon sx={{ color: COLORS.brown }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
            Payment Methods ({paymentMethods.length})
          </Typography>
        </Box>
        <IconButton size="small">
          {show ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={show} timeout="auto" unmountOnExit>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {paymentMethods.map(pm => (
            <Paper
              key={pm.id}
              sx={{
                p: 2, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, bgcolor: QUIZ.color.warmWhite,
                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2,
              }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.brownDeep }}>
                    {pm.payment_details?.brand || "Card"} **** {pm.payment_details?.last4 || "****"}
                  </Typography>
                  {pm.default && (
                    <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: 14 }} />} label="Default"
                      sx={{ bgcolor: COLORS.greenLight, color: COLORS.green, fontWeight: 600, fontSize: 10, height: 20 }} />
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
                  Expires {pm.payment_details?.exp_month || "--"}/{pm.payment_details?.exp_year || "----"}
                  {pm.processor_name && ` • ${pm.processor_name}`}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onOpenModal("paymentUpdate", pm as any)}
                  sx={{ fontSize: 11, textTransform: "none" }}
                >
                  Send Update
                </Button>
                {!pm.default && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => onOpenModal("deletePayment", pm as any)}
                    sx={{ fontSize: 11, textTransform: "none" }}
                  >
                    Delete
                  </Button>
                )}
                <Chip
                  label={pm.status || "Unknown"}
                  size="small"
                  sx={{
                    bgcolor: pm.status?.toLowerCase() === "valid" ? COLORS.greenLight : COLORS.redLight,
                    color: pm.status?.toLowerCase() === "valid" ? COLORS.green : COLORS.red,
                    fontWeight: 600, fontSize: 11,
                  }}
                />
              </Box>
            </Paper>
          ))}
        </Stack>
      </Collapse>
    </QuizStyledCard>
  );
}
